import useFocus from "@/lib/hooks/interactions/other/useFocus";
import {
  IconColorType,
  IconCounter,
  IconNameType,
  IconSizeType,
  LocaleDictionaryType,
  useHover,
  useLocales,
} from "@/lib/main";
import { CSSProperties } from "react";
import { Link, useLocation } from "react-router";

type Props = {
  count?: number;
  disabled?: boolean;
  iconColor?: IconColorType;
  iconName: IconNameType;
  linkTitle?: LocaleDictionaryType;
  size?: IconSizeType;
  style?: CSSProperties;
  to: string;
};

export default function IconLinkCounter({
  count,
  iconColor,
  iconName,
  linkTitle,
  size,
  style,
  to,
}: Props) {
  const l = useLocales();
  const location = useLocation();
  const { isHovered, onMouseEnter, onMouseLeave } = useHover();
  const { isFocused, onFocus, onBlur } = useFocus();

  const hasTouchLikePrimaryInput =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none), (pointer: coarse)").matches;

  const shouldApplyInteractiveState = !hasTouchLikePrimaryInput;

  const isSelected = to === location.pathname;
  const effectiveIsHovered = shouldApplyInteractiveState && isHovered;
  const effectiveIsFocused = shouldApplyInteractiveState && isFocused;
  const effectiveIsSelected = shouldApplyInteractiveState && isSelected;

  const linkStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    border: "none",
    borderRadius: "50%",

    backgroundColor: effectiveIsHovered
      ? "var(--color-icon-hover)"
      : effectiveIsFocused
        ? "var(--color-icon-focus)"
        : effectiveIsSelected
          ? "var(--color-icon-selected)"
          : "transparent",
    ...style,
  };

  return (
    <Link
      aria-label={linkTitle ? l(linkTitle) : iconName}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={linkStyle}
      title={linkTitle ? l(linkTitle) : ""}
      to={to}
    >
      <IconCounter
        count={count || 0}
        iconColor={iconColor || "secondary"}
        iconName={iconName}
        size={size}
      />
    </Link>
  );
}
